import React from 'react';
import { Button, FormControl, MenuItem } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import { set } from 'lodash';
import { useQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import { useAllCountries } from 'src/common/hooks';
import { useTranslation } from 'react-i18next';

type LcaRegion = {
  iso_code: string;
  name: string;
};

export default function Location({
  setValues,
  values,
}: ProductInformationProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'location' });
  const handleLocation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, production_location: e.target.value });
  };

  const handleRegion = (e: { target: { value: string } }, i: number) => {
    const temp = { ...values };
    set(temp, `distribution_regions[${i}].iso_code`, e.target.value);
    setValues(temp);
  };

  const handleCountry = (e: { target: { value: string } }) => {
    setValues({ ...values, production_country: { iso_code: e.target.value } });
  };

  const addRegion = () => {
    setValues({
      ...values,
      distribution_regions: [
        ...values.distribution_regions,
        { iso_code: '', name: '' },
      ],
    });
  };

  const removeRegion = (i: number) => {
    setValues({
      ...values,
      distribution_regions: values.distribution_regions.splice(i - 1, 1),
    });
  };

  const { data: regionsData } = useQuery('getRegions', () => {
    return client.get<LcaRegion[]>('/web/lca/regions');
  });

  const { data: countriesData } = useAllCountries();

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      {values.distribution_regions.map((_: any, i: number) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
          }}
        >
          <FormControl fullWidth>
            <InputLabel>{t('distributionRegion')}</InputLabel>
            <Select
              value={values.distribution_regions[i].iso_code}
              label={t('distributionRegion')}
              onChange={(event) => handleRegion(event, i)}
            >
              {regionsData?.data.map(
                (el: { iso_code: string; name: string }) => (
                  <MenuItem key={el.iso_code} value={el.iso_code}>
                    {el.name}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="secondary"
            disabled={values.distribution_regions[i].iso_code === ''}
            onClick={() => removeRegion(i)}
          >
            {t('remove')}
          </Button>
        </div>
      ))}
      <div>
        <Button
          variant="secondary"
          size="small"
          onClick={addRegion}
          disabled={
            values.distribution_regions[values.distribution_regions.length - 1]
              .iso_code === ''
          }
        >
          {t('addDistributionRegion')}
        </Button>
      </div>
      <FormControl fullWidth>
        <InputLabel>{t('countryOfProduction')}</InputLabel>
        <Select
          value={values.production_country.iso_code}
          label={t('countryOfProduction')}
          onChange={(event) => handleCountry(event)}
        >
          {countriesData.map((el: { iso_code: string; name: string }) => (
            <MenuItem key={el.iso_code} value={el.iso_code}>
              {el.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <StyledTextField
        fullWidth
        label={t('locationOfProduction')}
        name="production_location"
        value={values.production_location}
        onChange={handleLocation}
      />
    </div>
  );
}
